import React from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import QuickFact from '@rover/react-lib/src/pages/landing/components/QuickFact';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import {
  ROVER_QUICK_FACTS_DATA_CITATION,
  Superscript,
} from '@rover/react-lib/src/pages/shared/FooterLinks/FactCitations';
import { AgeEnum } from '@rover/rsdk/src/apiClient/latest/model/ageEnum';
import type { LocalizedBrandServicePetType } from '@rover/rsdk/src/apiClient/latest/model/localizedBrandServicePetType';
import type { PetDetails } from '@rover/rsdk/src/apiClient/latest/model/petDetails';
import { PetTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/petTypeEnum';
import getBrandDisplayName from '@rover/shared/js/utils/getBrandDisplayName';
import { PetType } from '@rover/types/src/Pet';
import { ServiceTypeSlug } from '@rover/types/src/ServiceType';

import { PageSectionId, useSectionRef } from '../../../SeoLocation/hooks/useSectionRef';
import catLookingUpwardImage from '../../components/images/cat-looking-upward.png';
import dogLookingUpwardImage from '../../components/images/dog-looking-upward.png';
import RoverProtectCondensed from '../../components/RoverProtectCondensed';
import ServiceLandingBenefits from '../../components/ServiceLandingBenefits';
import ServiceLandingFaqList from '../../components/ServiceLandingFaqList';
import ServiceLandingFooterLinks from '../../components/ServiceLandingFooterLinks';
import ServiceLandingHero from '../../components/ServiceLandingHero';
import ServiceLandingHowBrandWorks from '../../components/ServiceLandingHowBrandWorks';
import ServiceLandingPageWrapper from '../../components/ServiceLandingPageWrapper';
import ServiceLandingSearchResultsCarousel from '../../components/ServiceLandingSearchResultsCarousel';
import ServiceLandingSection, {
  SERVICE_LANDING_SECTION_MARGIN_Y,
} from '../../components/ServiceLandingSection';
import ServiceLandingServicesWrapper from '../../components/ServiceLandingServicesWrapper';
import type { ServiceLandingTemplateProps } from '../../types';
import { getServiceLandingSearchFilters } from '../../utilities/getServiceLandingSearchFilters';

import { getServiceLandingQuickFacts } from './utilities/getServiceLandingQuickFacts';

const getPagePetType = (
  petType: LocalizedBrandServicePetType | undefined,
  age: PetDetails['age']
): PetType | undefined => {
  if (petType === PetTypeEnum.cat) return PetType.CAT;
  if (petType === PetTypeEnum.dog) return age === AgeEnum.adolescent ? PetType.PUPPY : PetType.DOG;
  return undefined;
};

const getOtherServicesImage = (petType: LocalizedBrandServicePetType | undefined): string =>
  petType === PetTypeEnum.cat ? catLookingUpwardImage : dogLookingUpwardImage;

const getOtherServicesHeading = (): MessageDescriptor => {
  const brandDisplayName = getBrandDisplayName();
  return msg`${brandDisplayName} offers many services to meet your pet care needs, including:`;
};

const getFaqsHeading = (serviceTypeName: string): MessageDescriptor =>
  msg({
    message: `${serviceTypeName} FAQs`,
    comment:
      'Section heading. serviceTypeName is a title-cased, localized service keyword of unknown gender that may be a phrase (e.g. "Kurzfristig buchen"), so use a form that needs no agreement with it, such as "FAQ : {serviceTypeName}".',
  });

const ServiceLandingPageTemplate: React.FC<ServiceLandingTemplateProps> = ({
  serviceLandingData,
  descriptor,
  countryConfig,
  locale,
  serviceTypes,
  citiesAndNeighborhoodsLinks,
  countryFooterLinks,
  countryRegionLinks,
  serviceTypeSlug,
  searchResults,
  otherServiceLinks,
}) => {
  const { i18n } = useLingui();
  const otherServicesSectionRef = useSectionRef(PageSectionId.OtherServices);
  const faqsSectionRef = useSectionRef(PageSectionId.FAQs);

  const {
    h1,
    h2,
    seoTitle,
    seoMetaDescription,
    internalServiceType,
    petDetails,
    petType,
    serviceBenefitItems,
    serviceBenefitsHeading,
    sentenceCasedServiceTypeName,
    titleCasedServiceTypeName,
    indexable,
  } = serviceLandingData;
  const {
    displayName: serviceTypeName,
    slug: internalServiceTypeSlug,
    providerNounPlural,
  } = internalServiceType;
  const brandName = getBrandDisplayName();
  const isTraining = internalServiceTypeSlug === ServiceTypeSlug.DOG_TRAINING;
  const roverProtectSectionRef = useSectionRef(PageSectionId.RoverGuarantee);
  const pagePetType = getPagePetType(petType, petDetails.age);
  const quickFacts = getServiceLandingQuickFacts(internalServiceTypeSlug, petType, i18n.locale);

  const searchFilters = getServiceLandingSearchFilters(serviceLandingData);

  return (
    <ServiceLandingPageWrapper
      seoTitle={seoTitle}
      seoMetaDescription={seoMetaDescription}
      serviceTypeName={serviceTypeName}
      brandName={brandName}
      h1={h1}
      h2={h2}
      countryConfig={countryConfig}
      indexable={indexable}
    >
      <ServiceLandingHero
        h1={h1}
        h2={h2}
        locale={locale}
        serviceTypes={serviceTypes}
        internalServiceType={internalServiceType}
        initialSearchFilters={searchFilters}
        petType={petType}
        age={petDetails.age}
        internalServiceTypeSlug={internalServiceTypeSlug}
      />

      <RoverProtectCondensed
        countryConfig={countryConfig}
        isTraining={isTraining}
        htmlRef={roverProtectSectionRef}
        mt="0x"
        mb={SERVICE_LANDING_SECTION_MARGIN_Y}
      />

      <ServiceLandingBenefits
        internalServiceTypeSlug={internalServiceTypeSlug}
        petType={petType}
        age={petDetails.age}
        serviceBenefitItems={serviceBenefitItems}
        serviceBenefitsHeading={serviceBenefitsHeading}
      />

      <ServiceLandingSearchResultsCarousel
        serviceTypeName={sentenceCasedServiceTypeName || serviceTypeName}
        providerNounPlural={providerNounPlural}
        searchResults={searchResults}
        searchFilters={searchFilters}
        locale={locale}
        serviceTypeSlug={serviceTypeSlug}
        countryConfig={countryConfig}
      />

      {quickFacts && (
        <QuickFact themeColor="yellow" {...quickFacts.first.sticker}>
          {quickFacts.first.text}
          <Superscript>1</Superscript>
        </QuickFact>
      )}

      {/* QuickFact's 16x margins collapse into these, so the gaps on either side match. */}
      <ServiceLandingFaqList
        descriptor={descriptor}
        title={i18n._(getFaqsHeading(titleCasedServiceTypeName || serviceTypeName))}
        htmlRef={faqsSectionRef}
        mt="16x"
        mb="16x"
      />

      {quickFacts && (
        <QuickFact themeColor="pink" {...quickFacts.second.sticker}>
          {quickFacts.second.text}
          <Superscript>1</Superscript>
        </QuickFact>
      )}

      <ServiceLandingHowBrandWorks
        petType={pagePetType}
        serviceTypeName={sentenceCasedServiceTypeName ?? undefined}
        providerNounPlural={providerNounPlural}
      />

      {otherServiceLinks.length > 0 && (
        <ServiceLandingServicesWrapper
          htmlRef={otherServicesSectionRef}
          serviceLandingType={null}
          serviceLinksWithDisplayName={otherServiceLinks}
          imageSrc={getOtherServicesImage(petType)}
          mt={['12x', '24x', '0x']} // keep the legacy mobile/tablet gap; flush with HowBrandWorks on desktop only
        >
          {i18n._(getOtherServicesHeading())}
        </ServiceLandingServicesWrapper>
      )}

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout />
      </ServiceLandingSection>
      <ServiceLandingFooterLinks
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
        citations={quickFacts ? [i18n._(ROVER_QUICK_FACTS_DATA_CITATION)] : undefined}
      />
    </ServiceLandingPageWrapper>
  );
};

export default ServiceLandingPageTemplate;
